import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PARTIAL_LDM_MAX,
  VEHICLE_BODY_OPTIONS,
  sameVehicle,
  type VehicleProfile,
  type VehicleSuggestion,
} from './publicationsApi';
import { plNumber } from '../../shared/number';

/**
 * Pojazd w ofercie — w oknie publikacji, podpowiedziany z typu zlecenia.
 *
 * Marathon 30.09.2026: „Brak danych o typie nadwozia powoduje konieczność
 * ręcznego wyboru przy publikacji. – MI POSIADA WYŁĄCZNIE JUMBO TRUCK”.
 * Serwer podpowiada profil z typu zlecenia (bez typu — JUMBO) i mówi, skąd
 * podpowiedź; okno ją wstawia, trader może ją zmienić, a podpis źródła
 * mówi, czy idzie podpowiedź, czy jego wybór. Słownika giełd (kody zabudów)
 * tu nie ma — zna go serwer, a podgląd pokazuje, co pójdzie.
 */

/** Pole w edycji — metry częściówki jako tekst, bo trader je pisze. */
export interface VehicleDraft {
  body: string;
  ldm: string;
  forklift: boolean;
}

export const draftFromProfile = (p: VehicleProfile | null): VehicleDraft | null =>
  p ? { body: p.body, ldm: p.partialLdm === null ? '' : String(p.partialLdm), forklift: p.forklift } : null;

/** Profil z pola; `null` = metry częściówki niepoprawne. */
export function profileFromDraft(d: VehicleDraft): VehicleProfile | null {
  const text = d.ldm.trim().replace(',', '.');
  if (text === '') return { body: d.body, partialLdm: null, forklift: d.forklift };
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0 || n > PARTIAL_LDM_MAX) return null;
  return { body: d.body, partialLdm: n, forklift: d.forklift };
}

export const VehicleChoice = ({
  suggestion,
  value,
  onChange,
}: {
  suggestion: VehicleSuggestion;
  value: VehicleDraft | null;
  onChange: (d: VehicleDraft) => void;
}) => {
  const profile = value ? profileFromDraft(value) : null;
  const invalidLdm = value !== null && profile === null;
  const fromSuggestion = sameVehicle(profile, suggestion.profile);
  const draft = value ?? { body: '', ldm: '', forklift: false };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Pojazd w ofercie
      </Typography>
      {suggestion.profile === null && value === null && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {suggestion.note}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="pojazd-gielda">Typ pojazdu</InputLabel>
          <Select
            labelId="pojazd-gielda"
            label="Typ pojazdu"
            value={draft.body}
            onChange={(e) => onChange({ ...draft, body: String(e.target.value) })}
          >
            {VEHICLE_BODY_OPTIONS.map((o) => (
              <MenuItem key={o.kod} value={o.kod}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Ładunek częściowy — LDM (puste = całe auto)"
          value={draft.ldm}
          disabled={value === null}
          error={invalidLdm}
          helperText={invalidLdm ? `Wpisz liczbę większą od 0, najwyżej ${plNumber(PARTIAL_LDM_MAX)}.` : undefined}
          onChange={(e) => onChange({ ...draft, ldm: e.target.value })}
        />
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={draft.forklift}
            disabled={value === null}
            onChange={(e) => onChange({ ...draft, forklift: e.target.checked })}
          />
        }
        label="Wymagany wózek widłowy"
      />
      {/* Zdanie o rekomendacji przychodzi z API (`vehicle-profile.ts`); ekran
          dopisuje tylko, że trader ją zmienił (01.10.2026: „rekomendacja”,
          forma bezosobowa). Bez rekomendacji nie ma czego zmieniać. */}
      {value !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {suggestion.profile === null
            ? 'Pojazd wybrany ręcznie.'
            : fromSuggestion
              ? suggestion.note
              : `Zmieniono ręcznie. ${suggestion.note}`}
        </Typography>
      )}
    </Box>
  );
};
